import { eventNames, useCoreEvents } from '@carvana/core-events';
import { useCallback } from 'react';
import { Step } from '~/constants/common/AccountModal';
import {
  CTA_GUARD_ACTION,
  CtaGuard,
  CtaGuardOutcome,
  PURCHASE_CALLER_IDENTIFIER
} from '~/constants/common/ctaGuard';
import { useIsNative } from '~/context/common/DeviceContext';
import { useSendMessageToNative } from '~/context/common/NativeContext';
import type {
  OpenAccountModalPayload,
  StepConfiguration
} from '~/contracts/common';
import { logInfo } from '~/utilities/loggers';

declare global {
  interface Window {
    carvana?: {
      onLoginSuccess?: () => void;
    };
  }
}

type OpenAccountModalParams<Event> = StepConfiguration & {
  callerIdentifier?: string;
  nativeOptions?: {
    eventPayload?: Record<string, any>;
    eventDispatch: Event;
  };
  onSuccess?: () => Promise<void> | void;
  step?: Step;
};

/**
 * Mirrors core-events' own emit branching. It queues only when the event has never had a listener;
 * once mitt's `off` has run, the key survives with an empty handler array and the emit lands there
 * with no queue and no replay — the genuinely lossy case.
 */
function classifyDelivery(
  hasListenerKey: boolean,
  handlerCount: number
): CtaGuardOutcome {
  if (!hasListenerKey) return CtaGuardOutcome.Queued;

  if (handlerCount > 0) return CtaGuardOutcome.Delivered;

  return CtaGuardOutcome.Dropped;
}

export function useOpenAccountModal() {
  const { all, emit } = useCoreEvents();
  const isNative = useIsNative();
  const sendMessageToNative = useSendMessageToNative();

  return useCallback(
    <Event>({
      callerIdentifier,
      displayBtnText,
      displayHeading,
      displayTitle,
      nativeOptions,
      onSuccess,
      step
    }: OpenAccountModalParams<Event>) => {
      const stepConfiguration: StepConfiguration = {
        displayTitle,
        displayHeading,
        displayBtnText
      };

      const payload: OpenAccountModalPayload = {
        CallerIdentifier: callerIdentifier,
        OnAuthenticatedSuccess: onSuccess,
        ...(step === Step.CreateAccount
          ? {
              step: Step.CreateAccount,
              CreateAccount: stepConfiguration
            }
          : {
              step: Step.EnterEmail,
              EnterEmail: stepConfiguration
            })
      };

      // Only the purchase CTA belongs to the guard chain; SRP, Home, favorites and pre-order open
      // the same modal but are not part of it. Native is excluded because it has no web modal, so
      // every emit would report `queued` — a listener that will never attach.
      if (!isNative && callerIdentifier === PURCHASE_CALLER_IDENTIFIER) {
        const listenerCount =
          all.get(eventNames.accountModal.open)?.length ?? 0;
        // Counted but not reported: mitt delivers to '*' handlers too, so a wildcard subscriber
        // from any MFE on the shared emitter means the payload arrived, not `dropped`.
        const wildcardCount = all.get('*')?.length ?? 0;
        const hasListenerKey =
          all.has(eventNames.accountModal.open) || all.has('*');

        logInfo(CTA_GUARD_ACTION, {
          guard: CtaGuard.AccountModalEmit,
          listenerCount,
          outcome: classifyDelivery(
            hasListenerKey,
            listenerCount + wildcardCount
          )
        });
      }

      // A no-op on native: removeHeader is forced on for webviews, so the Header MFE and
      // CoreAccountModal never load and nothing subscribes. Not gated here because silencing it for
      // the other callers would need an audit of every webview surface first.
      emit(eventNames.accountModal.open, payload);

      if (nativeOptions) {
        const { eventPayload = {}, eventDispatch } = nativeOptions;

        sendMessageToNative(eventDispatch, eventPayload, {
          callback: () => {
            // Store the onSuccess handler in a global variable, so it can be called from the native app.
            window.carvana = window.carvana ?? {};
            window.carvana.onLoginSuccess = onSuccess;
          }
        });
      }
    },
    [all, emit, isNative, sendMessageToNative]
  );
}

export function useOpenedAccountModal() {
  const { emit } = useCoreEvents();

  return useCallback(() => {
    emit(eventNames.accountModal.opened);
  }, [emit]);
}

export function useCloseAccountModal() {
  const { emit } = useCoreEvents();

  return useCallback(() => {
    emit(eventNames.accountModal.close);
  }, [emit]);
}

export function useClosedAccountModal() {
  const { emit } = useCoreEvents();

  return useCallback(
    (method: string) => {
      emit(eventNames.accountModal.closed, { closedMethod: method });
    },
    [emit]
  );
}
